import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { get, set } from '@totalsoft/change-tracking-react';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { Card, IconButton, Autocomplete, TextField, Typography, DynamicField } from '@totalsoft/rocket-ui';
import { getDynamicFieldControlType } from 'utils/functions';
import { emptyArray, emptyString } from 'utils/constants';
import { pvtUnspecified } from './constants';
import { valueOrDefault } from 'utils/functions';
import { ADMIN_ITEMS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const PropertyGeneralDataComponent = ({ lens, validation, propertyValueTypes, properties, onSave, isDirty, isNewProperty }) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_ITEMS_WRITE);

  const filterProperties = useCallback(
    value => {
      const inputText = value?.toString()?.toUpperCase() || '';
      if (!inputText) return Promise.resolve([]);
      const filteredProperties = properties?.filter(({ propertyName }) => propertyName.toUpperCase().includes(inputText));
      return Promise.resolve(filteredProperties);
    },
    [properties]
  );

  const propertyValue = useMemo(
    () => properties?.find(({ propertyId }) => (lens.propertyId |> get) === propertyId),
    [lens?.propertyId, properties]
  );

  const propertyTypeCode = useMemo(
    () => properties?.find(({ propertyId }) => get(lens.propertyId) === propertyId)?.propertyTypeCode,
    [lens?.propertyId, properties]
  );

  const handleValueTypeChange = useCallback(
    value => {
      set(lens.propertyValueTypeId)(value.id);
      set(lens.propertyValueTypeCode)(value.code);
    },
    [lens.propertyValueTypeId, lens.propertyValueTypeCode]
  );

  return (
    <Card
      title={t('Items.Properties.GeneralData')}
      sx={{ mb: 3 }}
      actions={
        <IconButton
          color='transparent'
          type='save'
          tooltip={t('Items.Save')}
          fontSize='medium'
          onClick={onSave}
          disabled={!isDirty || (lens.saving |> get) || !canWrite}
        />
      }
    >
      <Grid container spacing={2} alignItems='flex-end'>
        <Grid item xs={12} md={3}>
          <Autocomplete
            id='propertyGeneralData_property'
            label={t('Items.Properties.Property')}
            value={propertyValue}
            labelKey='propertyName'
            valueKey='propertyId'
            onChange={value => set(lens.propertyId)(value.propertyId)}
            loadOptions={filterProperties}
            error={!isValid(validation.propertyId)}
            helperText={getErrors(validation.propertyId)}
            disabled={!isNewProperty || (lens.saving |> get) || !canWrite}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            disabled={(lens.saving |> get) || !canWrite}
            id='propertyGeneralData_valueType'
            fullWidth
            isSearchable
            required
            valueKey={'id'}
            labelKey={'code'}
            label={t('Items.Properties.ValueType')}
            value={{ id: lens.propertyValueTypeId |> get, code: lens.propertyValueTypeCode |> get }}
            onChange={handleValueTypeChange}
            options={propertyValueTypes || emptyArray}
            error={!isValid(validation.propertyValueTypeId)}
            helperText={getErrors(validation.propertyValueTypeId)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            disabled={(lens.saving |> get) || !canWrite}
            fullWidth
            label={t('Items.Properties.Comments')}
            value={get(lens.comments) || emptyString}
            onChange={event => set(lens.comments)(event)}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControlLabel
            control={
              <Switch
                disabled={(lens.saving |> get) || !canWrite}
                id='Items.Mandatory'
                checked={lens.mandatory |> get |> valueOrDefault(false)}
                color='secondary'
                onChange={event => set(lens.mandatory)(event.target.checked)}
              />
            }
            label={<Typography>{t('Items.Properties.Mandatory')}</Typography>}
          />
        </Grid>

        {(lens.propertyValueTypeCode |> get) === pvtUnspecified && (
          <Grid item xs={12}>
            <DynamicField
              id={`defaultValue`}
              disabled={!canWrite}
              controlType={getDynamicFieldControlType(propertyTypeCode)}
              label={t('Items.Properties.DefaultValue')}
              value={get(lens.defaultValue) || emptyString}
              onChange={event => set(lens.defaultValue)(event)}
            />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

PropertyGeneralDataComponent.propTypes = {
  lens: PropTypes.object,
  validation: PropTypes.object,
  propertyValueTypes: PropTypes.array,
  properties: PropTypes.array,
  onSave: PropTypes.func,
  isDirty: PropTypes.bool,
  isNewProperty: PropTypes.bool
};

export default PropertyGeneralDataComponent;
